import React, {useRef, useState, useMemo, useEffect, useCallback} from 'react'
import {useFormContext, Controller} from 'react-hook-form'
import {Box, Typography, LinearProgress, Tooltip, Button} from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, LIGHT_BG, PRIMARY_BLUE, SUCCESS, ERROR, withOpacity} from '../../styles/foundation/colors'

// Human-readable file size.
const fmtSize = (b) => {
  if (b == null) return ''
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * RHFFileField — react-hook-form file input.
 *
 * Behaviour is unchanged from the original (same Controller wiring, the same
 * `field.onChange` payload — a FileList for single, an Array<File> for multiple —
 * and the same `validateFileType` rules), so every existing consumer keeps working.
 * The UI is rebuilt on the design system with drag-and-drop, hover/drag-over/error/
 * success states, file chips (name + size + remove), image thumbnails, optional
 * progress, and keyboard accessibility.
 *
 * New (all optional, default off — backward compatible):
 *   uploading  — show the "uploading" state
 *   progress   — 0..100 for a determinate bar (indeterminate while `uploading` and no number)
 *   helperText — small hint under the prompt (defaults to the allowed file types)
 *   accept     — native picker filter (defaults to `fileType` so the picker matches validation)
 */
export default function RHFFileField({
  name,
  required = false,
  fileType,
  label = 'Click here to upload.',
  size = 3,
  variant = null,
  multiple = false,
  uploading = false,
  progress = null,
  helperText,
  accept,
}) {
  const {control, formState} = useFormContext()
  const {errors} = formState
  const inputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)

  const validateFileType = (file) => {
    if (!fileType) return true // No file type restriction
    if (Array.isArray(fileType)) return fileType.some((type) => file.name.endsWith(type))
    return file.name.endsWith(fileType)
  }

  const allowed = Array.isArray(fileType) ? fileType.join(', ') : fileType
  const acceptAttr = accept || (Array.isArray(fileType) ? fileType.join(',') : fileType) || undefined
  const hasError = Boolean(errors[name])

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? 'This field is required.' : false,
        validate: (files) => {
          if (files && files.length > 0 && !validateFileType(files[0])) {
            return `Invalid file type. Allowed: ${fileType}`
          }
          return true
        },
      }}
      render={({field}) => {
        const files = field.value && field.value.length > 0 ? Array.from(field.value) : []
        const hasFiles = files.length > 0

        const commit = (fileList) => {
          if (!fileList || fileList.length === 0) return
          // Preserve the original payload contract: Array for multiple, FileList for single.
          field.onChange(multiple ? Array.from(fileList) : fileList)
        }

        const openPicker = () => inputRef.current && inputRef.current.click()

        const removeAt = (idx, e) => {
          if (e) e.stopPropagation()
          if (multiple) {
            field.onChange(files.filter((_, i) => i !== idx))
          } else {
            field.onChange(null)
          }
          if (inputRef.current) inputRef.current.value = ''
        }

        return (
          <FileFieldView
            files={files}
            hasFiles={hasFiles}
            hasError={hasError}
            errorMsg={errors[name]?.message}
            dragOver={dragOver}
            setDragOver={setDragOver}
            label={label}
            helperText={helperText != null ? helperText : allowed ? `Allowed: ${allowed}` : ''}
            size={size}
            variant={variant}
            multiple={multiple}
            uploading={uploading}
            progress={progress}
            openPicker={openPicker}
            removeAt={removeAt}
            onDrop={(dropped) => {
              setDragOver(false)
              commit(dropped)
            }}
          >
            <input
              onChange={(e) => commit(e.target.files)}
              type='file'
              ref={inputRef}
              hidden
              multiple={multiple}
              accept={acceptAttr}
            />
          </FileFieldView>
        )
      }}
    />
  )
}

function FileFieldView({
  files,
  hasFiles,
  hasError,
  errorMsg,
  dragOver,
  setDragOver,
  label,
  helperText,
  size,
  variant,
  multiple,
  uploading,
  progress,
  openPicker,
  removeAt,
  onDrop,
  children,
}) {
  // Object URLs for image thumbnails — created per file, revoked on change/unmount.
  const previews = useMemo(
    () => files.map((f) => (f && f.type && f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
    [files],
  )
  useEffect(() => () => previews.forEach((u) => u && URL.revokeObjectURL(u)), [previews])

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openPicker()
      }
    },
    [openPicker],
  )

  // Border/background per state.
  const borderColor = hasError ? ERROR : dragOver ? PRIMARY_BLUE : hasFiles ? withOpacity(SUCCESS, 0.5) : BORDER
  const bg = hasError
    ? withOpacity(ERROR, 0.04)
    : dragOver
      ? withOpacity(PRIMARY_BLUE, 0.08)
      : variant === 'filled'
        ? LIGHT_BG
        : WHITE

  const padY = typeof size === 'number' ? Math.max(size, 3) * 6 : 20

  // Icon-badge accent: green once a file is held, brighter while dragging, blue otherwise.
  const accent = hasError ? ERROR : hasFiles ? SUCCESS : PRIMARY_BLUE
  const badgeBg = withOpacity(accent, dragOver ? 0.22 : 0.12)
  const headline = dragOver
    ? 'Drop your file here'
    : hasFiles && !multiple
      ? 'Replace file'
      : typeof label === 'string' && label !== 'Click here to upload.'
        ? label
        : 'Drag & drop your file here'

  return (
    <Box sx={{width: 1}}>
      {/* Dropzone */}
      <Box
        role='button'
        tabIndex={0}
        aria-label={typeof label === 'string' ? label : 'Upload file'}
        onClick={openPicker}
        onKeyDown={onKeyDown}
        onDragOver={(e) => {
          e.preventDefault()
          if (!dragOver) setDragOver(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setDragOver(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          onDrop(e.dataTransfer.files)
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1,
          px: 2,
          py: `${padY}px`,
          border: `1.5px dashed ${borderColor}`,
          borderRadius: '14px',
          backgroundColor: dragOver ? withOpacity(PRIMARY_BLUE, 0.06) : variant === 'filled' ? LIGHT_BG : '#FCFDFE',
          cursor: 'pointer',
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
          outline: 'none',
          '&:hover': {borderColor: hasError ? ERROR : PRIMARY_BLUE, backgroundColor: withOpacity(PRIMARY_BLUE, 0.05)},
          '&:focus-visible': {borderColor: PRIMARY_BLUE, boxShadow: `0 0 0 3px ${withOpacity(PRIMARY_BLUE, 0.18)}`},
        }}
      >
        {/* Icon badge */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: badgeBg,
            transition: 'background-color 0.15s ease',
          }}
        >
          <CloudUploadOutlinedIcon sx={{fontSize: 26, color: accent}} />
        </Box>

        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25}}>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY}}>
            {headline}
          </Typography>
          {!dragOver && (
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
              or click to browse
            </Typography>
          )}
        </Box>

        {!dragOver && (
          <Button
            variant='outlined'
            size='small'
            onClick={(e) => {
              e.stopPropagation()
              openPicker()
            }}
            sx={{
              mt: 0.25,
              textTransform: 'none',
              fontFamily: FONT,
              fontWeight: WEIGHT.semibold,
              fontSize: SIZE['2xs'],
              borderRadius: '8px',
              borderColor: withOpacity(PRIMARY_BLUE, 0.5),
              color: PRIMARY_BLUE,
              px: 1.75,
              '&:hover': {borderColor: PRIMARY_BLUE, backgroundColor: withOpacity(PRIMARY_BLUE, 0.06)},
            }}
          >
            {hasFiles && !multiple ? 'Choose another' : 'Browse files'}
          </Button>
        )}

        {helperText && (
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25}}>
            {helperText}
          </Typography>
        )}
        {children}
      </Box>

      {/* Progress */}
      {uploading && (
        <Box sx={{mt: 1.25}}>
          <LinearProgress
            variant={typeof progress === 'number' ? 'determinate' : 'indeterminate'}
            value={typeof progress === 'number' ? progress : undefined}
            sx={{height: 6, borderRadius: 999, backgroundColor: withOpacity(PRIMARY_BLUE, 0.12)}}
          />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5}}>
            Uploading{typeof progress === 'number' ? ` ${Math.round(progress)}%` : '…'}
          </Typography>
        </Box>
      )}

      {/* Selected file chips */}
      {hasFiles && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mt: 1.25}}>
          {files.map((file, i) => (
            <Box
              key={`${file.name}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1,
                border: `1px solid ${BORDER}`,
                borderRadius: '10px',
                backgroundColor: WHITE,
              }}
            >
              {previews[i] ? (
                <Box
                  component='img'
                  src={previews[i]}
                  alt={file.name}
                  sx={{width: 36, height: 36, borderRadius: '8px', objectFit: 'cover', flexShrink: 0}}
                />
              ) : (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: withOpacity(PRIMARY_BLUE, 0.08),
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{fontSize: 18, color: PRIMARY_BLUE}} />
                </Box>
              )}

              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: SIZE.xs,
                    fontWeight: WEIGHT.semibold,
                    color: NAVY,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={file.name}
                >
                  {file.name}
                </Typography>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
                  {fmtSize(file.size)}
                </Typography>
              </Box>

              {!uploading && (
                <CheckCircleRoundedIcon sx={{fontSize: 18, color: SUCCESS, flexShrink: 0}} />
              )}

              <Tooltip title='Remove'>
                <Box
                  component='span'
                  role='button'
                  aria-label={`Remove ${file.name}`}
                  tabIndex={0}
                  onClick={(e) => removeAt(i, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      removeAt(i, e)
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    borderRadius: '7px',
                    color: SUBTLE,
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                    '&:hover': {backgroundColor: withOpacity(ERROR, 0.1), color: ERROR},
                  }}
                >
                  <CloseRoundedIcon sx={{fontSize: 16}} />
                </Box>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* Error */}
      {hasError && (
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: ERROR, mt: 0.75, display: 'block'}}>
          {errorMsg}
        </Typography>
      )}
    </Box>
  )
}
